import type { Location } from 'react-router-dom';
import { isValidLanding } from '@/lib/user-preferences/types';

/**
 * W16-F5-S2 — pick the post-login redirect target.
 *
 * Order of precedence:
 *   1. Deep-link the user was bounced from (`location.state.from`).
 *   2. Saved user landing preference (validated via `isValidLanding`).
 *   3. `/dashboard` fallback.
 *
 * Pure function — no react-query, no localStorage. The caller resolves
 * `savedLanding` from whatever cache it has (UserPreferences provider
 * is already mounted in the app shell, so the hook's synchronous
 * cached value is available immediately on most navigations).
 */
export interface LandingResolverInput {
  /** react-router location state (post-redirect from `<AuthGuard>`). */
  location: Pick<Location, 'state'> | null;
  /** The user's saved landing preference, or `undefined`/`null`. */
  savedLanding: string | null | undefined;
}

const DEFAULT_FALLBACK = '/dashboard';

export function resolveLandingTarget({
  location,
  savedLanding,
}: LandingResolverInput): string {
  // 1) Deep-link priority — caller might have been bounced from a
  //    protected route. The state shape is `{ from: { pathname } }`.
  const state = location?.state as
    | { from?: { pathname?: string } }
    | null
    | undefined;
  const from = state?.from?.pathname;
  if (typeof from === 'string' && from && from !== '/login' && from !== '/') {
    return from;
  }
  // 2) Saved preference — validated. Invalid persisted values fall through.
  if (savedLanding && isValidLanding(savedLanding)) {
    return savedLanding;
  }
  if (savedLanding && typeof console !== 'undefined') {
    console.warn(
      `[landing] saved preference "${savedLanding}" is not a known route — falling back to ${DEFAULT_FALLBACK}`,
    );
  }
  // 3) Final fallback.
  return DEFAULT_FALLBACK;
}
